import { ReactElement, useState } from "react";
import {
  LoadingIndicator,
  SvgIcon,
  SvgNames,
} from "@sunrun/experience-ui-components";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "@emotion/styled/macro";
import sunrunLogoIcon from "assets/images/SR Logo.svg";
import discOutlineIcon from "assets/images/disc-outline.svg";
import academyIcon from "assets/images/academy_icon.svg";
import { NavButton } from "components/molecules/NavButton";
import { Icon } from "components/atoms/Icon";
import { NavigateBackHeader } from "components/atoms/NavigateBackHeader";
import type { Path } from "router";
import { maxWidth } from "helpers/styleConstants";
import { NotificationsButton } from "components/molecules/NotificationsButton";
import { NavigationItem, useNavigationItems } from "adapters/navigation";
import { MobileMenuOverlay } from "components/molecules/MobileMenuOverlay";
import { MessagesButton } from "components/molecules/MessagesButton";
import { MeganButton } from "components/molecules/MeganButton";
import { PerformanceButton } from "components/molecules/PerformanceButton";
import { trackNavigationItem } from "utils/navigationTracking";
import { DevMoreMenuItem, DevNavButton } from "features/devMenu/DevMenu";

type NavListItemProps = {
  href: Path;
  icon?: () => ReactElement;
  isActive?: boolean;
  isMobile: boolean;
  onClick: () => void;
  text: string;
  id: string;
};

const NavListItem = ({
  href,
  icon,
  isMobile,
  onClick,
  text,
  id,
}: NavListItemProps) => {
  const { pathname } = useLocation();
  const isActive = pathname.startsWith(href);

  return (
    <NavListItemContainer isMobile={isMobile}>
      <NavButton
        dataTestid={`menu-item-${id}`}
        href={href}
        icon={icon}
        onClick={onClick}
        isActive={isActive}
      >
        {text}
      </NavButton>
    </NavListItemContainer>
  );
};

const SideNav = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNavigateBack, setShowNavigateBack] = useState(false);
  const [currentPageTitle, setCurrentPageTitle] = useState("");
  const isMobile = useMediaQuery({ maxWidth: maxWidth });
  const { navigationItems, loading } = useNavigationItems();
  const navigate = useNavigate();

  // Filter main navigation items (excluding secondary group items)
  const mainNavItems = navigationItems.filter(
    (item) =>
      !["userGuides", "support", "notifications", "profile"].includes(item.id)
  );

  // Filter secondary navigation items (excluding main group items)
  const secondaryNavItems = navigationItems.filter((item) =>
    ["userGuides", "support", "notifications", "profile"].includes(item.id)
  );

  // Get first 4 items for mobile bottom nav
  const mobileNavItems = mainNavItems.slice(0, 4);
  const hasMoreItems = mainNavItems.length >= 4;

  // Move all hooks to top level - no conditional hooks
  const handleMoreClick = () => {
    setShowMobileMenu(true);
  };

  const handleCloseMobileMenu = () => {
    setShowMobileMenu(false);
  };

  const handleMobileNavItemClick = (item: NavigationItem) => () => {
    trackNavigationItem(item, "nav_press");
    if (showMobileMenu) {
      setShowMobileMenu(false);
    }
  };

  const handleMobileMenuOverlayItemClick = (item: NavigationItem) => {
    trackNavigationItem(item, "mobile_menu");
    setShowMobileMenu(false);
    setShowNavigateBack(true);
    setCurrentPageTitle(item.label);
    navigate(item.path);
  };

  const handleNavigateBackClick = () => {
    setShowNavigateBack(false);
    setShowMobileMenu(true);
  };

  // Show NavigateBackHeader if we're in navigate back mode on mobile
  if (isMobile && showNavigateBack) {
    return (
      <NavigateBackHeader
        data-testid="navigate-back-header"
        title={currentPageTitle}
        onBack={handleNavigateBackClick}
      />
    );
  }

  return (
    <>
      <SideNavContainer data-testid="sidenav-container" isMobile={isMobile}>
        {!isMobile && <DevNavButton />}
        {/* Desktop: Show logo */}
        {!isMobile && (
          <SideNavLogoLink isMobile={isMobile}>
            <NavButton
              href="/"
              icon={() => (
                <Icon
                  src={sunrunLogoIcon}
                  alt={"logo"}
                  size="small"
                  data-testid="sidenav-item-home"
                />
              )}
            />
          </SideNavLogoLink>
        )}

        <SideNavLinks isMobile={isMobile}>
          <SideNavLinkGroup isMobile={isMobile} grow={!isMobile}>
            {loading ? (
              <StyledLoadingSpinner showLoadingMessage={false} />
            ) : (
              <>
                {/* Mobile: Show only first 4 items + More button */}
                {isMobile
                  ? mobileNavItems.map((item) => (
                      <NavListItem
                        key={`${item.id}-mobile`}
                        href={item.path}
                        icon={() => getIconForNavItem(item)}
                        isMobile={isMobile}
                        onClick={handleMobileNavItemClick(item)}
                        text={item.label}
                        id={item.id}
                      />
                    ))
                  : /* Desktop: Show all main nav items */
                    mainNavItems.map((item) => (
                      <NavListItem
                        key={item.id}
                        href={item.path}
                        icon={() => getIconForNavItem(item)}
                        isMobile={isMobile}
                        onClick={() => trackNavigationItem(item, "nav_press")}
                        text={item.label}
                        id={item.id}
                      />
                    ))}

                {/* Mobile: Add "More" button if there are more than 4 items */}
                {isMobile && hasMoreItems && (
                  <MoreButton
                    data-testid="more-button"
                    onClick={handleMoreClick}
                    isMobile={isMobile}
                  >
                    <SvgIcon
                      name={SvgNames.Menu}
                      width="24"
                      height="24"
                      color="white"
                    />
                    <NavText>More</NavText>
                  </MoreButton>
                )}
              </>
            )}
          </SideNavLinkGroup>

          {/* Desktop: Show bottom navigation group */}
          {!isMobile && (
            <SideNavLinkGroup isMobile={isMobile}>
              {!loading &&
                secondaryNavItems.map((item) => (
                  <NavListItem
                    key={item.id}
                    href={item.path}
                    icon={() => getIconForNavItem(item)}
                    isMobile={isMobile}
                    onClick={() => trackNavigationItem(item, "nav_press")}
                    text={item.label}
                    id={item.id}
                  />
                ))}
            </SideNavLinkGroup>
          )}
        </SideNavLinks>
      </SideNavContainer>

      <MobileMenuOverlay
        data-testid="mobile-menu-overlay"
        isOpen={showMobileMenu}
        onClose={handleCloseMobileMenu}
        remainingNavItems={mainNavItems.slice(4)}
        secondaryNavItems={secondaryNavItems}
        getIconForNavItem={getIconForNavItem}
        onItemClick={handleMobileMenuOverlayItemClick}
        extraItems={<DevMoreMenuItem onOpened={handleCloseMobileMenu} />}
      />
    </>
  );
};

const getIconForNavItem = (item: NavigationItem): ReactElement => {
  if (item.id === "notifications") {
    return <NotificationsButton />;
  }

  if (item.id === "messages") {
    return <MessagesButton />;
  }

  if (item.id === "megan") {
    return <MeganButton />;
  }

  if (item.id === "performance") {
    return <PerformanceButton />;
  }

  if (item.id === "salesMap") {
    return (
      <Icon
        src={discOutlineIcon}
        alt={item.label}
        width="24"
        height="24"
        testId="sidenav-item-salesMap"
      />
    );
  }

  if (item.id === "academy") {
    return (
      <Icon
        src={academyIcon}
        alt={item.label}
        width="24"
        height="24"
        testId="sidenav-item-academy"
      />
    );
  }

  const iconName = Object.values(SvgNames).includes(item.svgName as SvgNames)
    ? (item.svgName as SvgNames)
    : SvgNames.File;

  return (
    <SvgIcon
      name={iconName}
      width="24"
      height="24"
      color="white"
      data-testid={`sidenav-item-${item.id}`}
    />
  );
};

const SideNavContainer = styled.nav<{ isMobile: boolean }>`
  display: inline-flex;
  flex-direction: ${(props) => (props.isMobile ? "row" : "column")};
  justify-content: space-between;
  align-items: center;
  gap: ${(props) => (props.isMobile ? "6px" : "8px")};
  flex-shrink: 0;
  position: fixed;
  bottom: ${(props) => (props.isMobile ? "0" : "auto")};
  padding: ${(props) => (props.isMobile ? "8px" : "64px 4px")};
  width: ${(props) => (props.isMobile ? "100%" : "84px")};
  height: ${(props) => (props.isMobile ? "72px" : "100vh")};
  background-color: #101426;
  z-index: 100;
  overflow: hidden;
`;

const SideNavLogoLink = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  padding: 0 0 24px 0;
`;

const SideNavLinks = styled.div<{ isMobile: boolean }>`
  display: flex;
  height: ${(props) => (props.isMobile ? "auto" : "100%")};
  flex-direction: ${(props) => (props.isMobile ? "row" : "column")};
  justify-content: ${(props) =>
    props.isMobile ? "space-around" : "flex-start"};
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;

  -ms-overflow-style: none;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const SideNavLinkGroup = styled.div<{
  isMobile: boolean;
  grow?: boolean;
}>`
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? "row" : "column")};
  flex-grow: ${(props) => (props.grow ? "1" : "0")};
  align-items: center;
  justify-content: ${(props) =>
    props.isMobile ? "space-between" : "flex-start"};
  gap: 8px;
  padding: 0;
  width: ${(props) => (props.isMobile ? "100%" : "auto")};
`;

const NavListItemContainer = styled.div<{ isMobile: boolean }>`
  position: relative;
  width: ${(props) => (props.isMobile ? "67px" : "76px")};
  height: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  flex: ${(props) => (props.isMobile ? "1" : "none")};
`;

const StyledLoadingSpinner = styled(LoadingIndicator)`
  width: 25px;
  margin: 10px 0px 20px 0px;
`;

const NavText = styled.span`
  font-size: 12px;
  color: white;
  margin-top: 4px;
`;

const MoreButton = styled.button<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 67px;
  height: 56px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  border-radius: 12px;
  flex: 1;
  padding: 0;

  &:hover {
    background-color: #22263a;
  }

  svg {
    margin-bottom: 4px;
  }
`;

export { SideNav };
